import { useRef, useState } from 'react'
import { AlertTriangle, Download, FileUp, Loader2 } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { Modal, errorMessage, primaryBtn, secondaryBtn } from './shared'

const ACTION_LABEL = { add: 'Add', change_role: 'Change role', unchanged: 'No change' }
const ACTION_CLS = {
  add: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  change_role: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  unchanged: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
}

function templateCsv(code) {
  return [
    'email,opco',
    `jan.jansen@example.com,${code}`,
    `piet.pieters@example.com,${code}`,
    '',
  ].join('\n')
}

function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Could not read the file'))
    reader.readAsText(file)
  })
}

function downloadTemplate(code) {
  const url = URL.createObjectURL(new Blob([templateCsv(code)], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'opco-members-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

// Assign people who are already in Xtract to OpCos from a CSV
// (email,opco): choose a file, review what each row would do, then
// confirm. All-or-nothing: Import stays off while any row has an error.
// Rows without an opco go to this panel's OpCo. An Admin can only add members
// to the OpCo they manage (the server enforces it).
export default function MemberImportModal({ opco, onImported, onClose }) {
  const fileInput = useRef(null)
  const [fileName, setFileName] = useState('')
  const [csv, setCsv] = useState('')
  const [plan, setPlan] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setBusy(true)
    setError(null)
    try {
      const text = await readFile(file)
      setCsv(text)
      const res = await opcoApi.previewMemberImport(text, { opcoId: opco.id })
      setPlan(res.data)
    } catch (err) {
      setPlan(null)
      setError(errorMessage(err, 'Could not read the file'))
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await opcoApi.importMembers(csv, { opcoId: opco.id })
      onImported?.(res.data)
    } catch (err) {
      // 422 carries the plan, 409 means memberships changed meanwhile.
      if (err?.response?.data?.rows) setPlan(err.response.data)
      setError(errorMessage(err, 'Import failed'))
      setBusy(false)
    }
  }

  const s = plan?.summary
  const nothingToDo = s && s.add === 0 && s.change_role === 0

  return (
    <Modal
      title="Assign users from CSV"
      wide
      labelledBy="opco-member-import-title"
      onClose={busy ? undefined : onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy} className={secondaryBtn}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !plan?.can_apply || nothingToDo}
            className={primaryBtn}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Import
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          One row per person: <span className="font-mono text-xs">email,opco</span>. Rows without an OpCo are
          added to {opco.name} as members; to make someone an Admin, give them the Admin role on the Users page.
          Only people who are already in Xtract can be assigned; invite new people first.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="member-import-file" />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={busy} className={secondaryBtn}>
            <FileUp className="h-4 w-4" /> {fileName ? 'Choose another file' : 'Choose CSV file'}
          </button>
          {fileName && <span className="text-sm text-gray-500 dark:text-gray-400">{fileName}</span>}
          <button type="button" onClick={() => downloadTemplate(opco.code)} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 hover:underline dark:text-navy-300">
            <Download className="h-3.5 w-3.5" /> Download template
          </button>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</p>
        )}

        {plan && (
          <div className="space-y-3" data-testid="member-import-preview">
            <div className="flex flex-wrap gap-2 text-xs font-semibold">
              <span className="rounded-full bg-green-100 px-2.5 py-1 text-green-700 dark:bg-green-900/30 dark:text-green-400">{s.add} to add</span>
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{s.change_role} role changes</span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-600 dark:bg-gray-800 dark:text-gray-400">{s.unchanged} unchanged</span>
              {s.rows_with_errors > 0 && (
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700 dark:bg-red-900/30 dark:text-red-400">{s.rows_with_errors} with errors</span>
              )}
            </div>
            {!plan.can_apply && (
              <p className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Nothing is imported until every row is valid. Fix the file and choose it again.
              </p>
            )}
            {plan.can_apply && nothingToDo && (
              <p className="text-sm text-gray-500 dark:text-gray-400">Everyone in this file is already assigned. Nothing to import.</p>
            )}
            {plan.warnings?.map((w) => (
              <p key={w} className="text-xs text-amber-700 dark:text-amber-400">{w}</p>
            ))}
            <div className="max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Line</th>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">OpCo</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.rows.map((r) => (
                    <tr key={`${r.line}-${r.email}-${r.opco_code}`} className={`border-t border-gray-100 align-top dark:border-gray-800 ${r.errors.length ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                      <td className="px-3 py-2 text-gray-400">{r.line}</td>
                      <td className="px-3 py-2">
                        <div>{r.username || r.email}</div>
                        {r.username && <div className="text-xs text-gray-400">{r.email}</div>}
                        {r.errors.map((e) => (
                          <p key={e} className="mt-1 text-xs text-red-600 dark:text-red-400">{e}</p>
                        ))}
                        {r.warnings.map((w) => (
                          <p key={w} className="mt-1 text-xs text-amber-700 dark:text-amber-400">{w}</p>
                        ))}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.opco_code}</td>
                      <td className="px-3 py-2 capitalize">
                        {r.role}
                        {r.action === 'change_role' && <div className="text-xs normal-case text-gray-400">was: {r.current_role}</div>}
                      </td>
                      <td className="px-3 py-2">
                        {!r.errors.length && (
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ACTION_CLS[r.action]}`}>{ACTION_LABEL[r.action]}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
